import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TextField, AreaField, SelectField, SwitchField, EditorDialog, EmptyState, Spinner } from "./AdminShared";

const EMPTY = {
  category: "", owner: "third-party", organization: "", program: "", featured: false,
  short_description: "", description: "", eligibility: "", geography: "National",
  deadline: "", deadline_type: "rolling", application_requirements: "", application_link: "",
  last_verified: new Date().toISOString().slice(0, 10),
};

export const ResourcesManager = () => {
  const [rows, setRows] = useState(null);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([api.get("/resources"), api.get("/categories")])
    .then(([r, c]) => { setRows(r.data); setCats(c.data); }).catch(() => toast.error("Failed to load resources"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    const body = { ...editing, deadline: editing.deadline || null };
    try {
      if (editing.id) await api.put(`/resources/${editing.id}`, body); else await api.post("/resources", body);
      toast.success(editing.id ? "Resource updated" : "Resource created");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.program}"?`)) return;
    await api.delete(`/resources/${r.id}`); toast.success("Resource deleted"); load();
  };

  const set = (k) => (v) => setEditing((e) => ({ ...e, [k]: v }));
  const filtered = (rows || []).filter((r) => !q || `${r.program} ${r.organization}`.toLowerCase().includes(q.toLowerCase()));
  const catOptions = cats.map((c) => ({ value: c.slug, label: c.name }));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="resources-manager">
      <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <Input placeholder="Search resources..." value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-xs" data-testid="resources-search-input" />
        <Button onClick={() => setEditing({ ...EMPTY, category: cats[0]?.slug || "" })} className="rounded-full bg-gold hover:bg-gold-light text-white" data-testid="resource-add-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Resource
        </Button>
      </div>
      {!rows ? <Spinner /> : filtered.length === 0 ? <EmptyState text="No resources found." /> : (
        <div className="divide-y divide-slate-100">
          {filtered.map((r) => (
            <div key={r.id} className="p-4 flex items-start gap-4 hover:bg-slate-50" data-testid={`resource-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {r.featured && <Star className="h-4 w-4 text-gold fill-gold" />}
                  <span className="font-semibold text-navy-900 truncate">{r.program}</span>
                  <Badge variant="outline" className="text-[10px]">{r.category}</Badge>
                  <Badge variant="outline" className="text-[10px] capitalize">{r.owner}</Badge>
                </div>
                <p className="text-sm text-slate-500 truncate">{r.organization} · {r.geography} · verified {r.last_verified}</p>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setEditing({ ...EMPTY, ...r })} data-testid={`resource-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(r)} data-testid={`resource-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditorDialog open onClose={() => setEditing(null)} title={editing.id ? "Edit Resource" : "New Resource"} onSave={save} saving={saving} testid="resource-editor">
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Program name" value={editing.program} onChange={set("program")} testid="resource-program-input" required />
            <TextField label="Organization" value={editing.organization} onChange={set("organization")} testid="resource-organization-input" required />
            <SelectField label="Category" value={editing.category} onChange={set("category")} options={catOptions} testid="resource-category-select" />
            <SelectField label="Owner" value={editing.owner} onChange={set("owner")} testid="resource-owner-select"
              options={[{ value: "foundation", label: "Foundation-operated" }, { value: "third-party", label: "Third-party" }]} />
            <TextField label="Geography" value={editing.geography} onChange={set("geography")} testid="resource-geography-input" />
            <SelectField label="Deadline type" value={editing.deadline_type} onChange={set("deadline_type")} testid="resource-deadline-type-select"
              options={[{ value: "rolling", label: "Rolling" }, { value: "fixed", label: "Fixed date" }, { value: "seasonal", label: "Seasonal" }]} />
            <TextField label="Deadline (optional)" value={editing.deadline} onChange={set("deadline")} testid="resource-deadline-input" />
            <TextField label="Last verified" type="date" value={editing.last_verified} onChange={set("last_verified")} testid="resource-last-verified-input" required />
          </div>
          <TextField label="Application link" value={editing.application_link} onChange={set("application_link")} testid="resource-link-input" required />
          <AreaField label="Short description" rows={2} value={editing.short_description} onChange={set("short_description")} testid="resource-short-desc-input" />
          <AreaField label="Full description" rows={4} value={editing.description} onChange={set("description")} testid="resource-desc-input" />
          <AreaField label="Eligibility" value={editing.eligibility} onChange={set("eligibility")} testid="resource-eligibility-input" />
          <AreaField label="Application requirements" value={editing.application_requirements} onChange={set("application_requirements")} testid="resource-requirements-input" />
          <SwitchField label="Featured on homepage" checked={editing.featured} onChange={set("featured")} testid="resource-featured-switch" />
        </EditorDialog>
      )}
    </div>
  );
};
